import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, Eye, ArrowLeft, Loader2, Paperclip, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PromotionalEmail() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [pools, setPools] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState({
    subject: '',
    body: '',
    signature: '',
    includeSocialMedia: false,
    includeOnlinePresence: false,
    pool_ids: null,  // null means send to all
    sendToAll: true
  });

  useEffect(() => {
    fetchBusinessProfile();
    fetchPools();
  }, []);

  const fetchPools = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/business/subscriber-pools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPools(response.data.pools);
    } catch (error) {
      console.error('Failed to fetch pools');
    }
  };

  const fetchBusinessProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessProfile(response.data);
      
      // Set default signature from business's saved signature or generate one
      if (response.data.email_signature) {
        setFormData(prev => ({
          ...prev,
          signature: response.data.email_signature
        }));
      } else if (response.data.business_name) {
        // Generate default signature if none exists
        const defaultSignature = generateDefaultSignature(response.data);
        setFormData(prev => ({
          ...prev,
          signature: defaultSignature
        }));
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  // Helper function to generate default signature
  const generateDefaultSignature = (profile) => {
    const lines = ['Best regards,'];
    
    if (profile.business_name) {
      lines.push(profile.business_name);
    }
    
    if (profile.owner_name) {
      lines.push(profile.owner_name);
    }
    
    if (profile.phone_number) {
      lines.push(`Phone: ${profile.phone_number}`);
    }
    
    if (profile.email) {
      lines.push(`Email: ${profile.email}`);
    }
    
    if (profile.website_url) {
      const cleanUrl = profile.website_url.replace('https://', '').replace('http://', '');
      lines.push(`Visit us at ${cleanUrl}`);
    }
    
    return lines.join('\n');
  };

  const handleSendEmail = async () => {
    if (!formData.subject || !formData.body) {
      toast.error('Please fill in subject and body');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('body', formData.body);
      formDataToSend.append('signature', formData.signature || '');
      formDataToSend.append('includeSocialMedia', formData.includeSocialMedia);
      formDataToSend.append('includeOnlinePresence', formData.includeOnlinePresence);
      
      // Send pool_ids as JSON string
      const poolIds = formData.sendToAll ? null : formData.pool_ids;
      formDataToSend.append('pool_ids', JSON.stringify(poolIds));
      
      // Append files
      attachments.forEach((file) => {
        formDataToSend.append('files', file);
      });

      const response = await axios.post(
        `${API}/business/send-promotional-email`,
        formDataToSend,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      toast.success(response.data.message || 'Promotional email sent successfully!');
      // Reset form - keep the saved signature
      const resetSignature = businessProfile?.email_signature || generateDefaultSignature(businessProfile);
      setFormData({
        subject: '',
        body: '',
        signature: resetSignature,
        includeSocialMedia: false,
        includeOnlinePresence: false,
        pool_ids: null,
        sendToAll: true
      });
      setAttachments([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments([...attachments, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const generatePreview = () => {
    let emailContent = formData.body.replace(/\n/g, '<br>');
    
    if (formData.signature) {
      emailContent += `<br><br>${formData.signature.replace(/\n/g, '<br>')}`;
    }

    if (formData.includeSocialMedia && businessProfile) {
      const socialLinks = [];
      if (businessProfile.facebook_url) {
        socialLinks.push(`<a href="${businessProfile.facebook_url}" style="color: #0F766E; text-decoration: none;">Facebook</a>`);
      }
      if (businessProfile.instagram_url) {
        socialLinks.push(`<a href="${businessProfile.instagram_url}" style="color: #0F766E; text-decoration: none;">Instagram</a>`);
      }
      if (businessProfile.linkedin_url) {
        socialLinks.push(`<a href="${businessProfile.linkedin_url}" style="color: #0F766E; text-decoration: none;">LinkedIn</a>`);
      }
      if (businessProfile.youtube_url) {
        socialLinks.push(`<a href="${businessProfile.youtube_url}" style="color: #0F766E; text-decoration: none;">YouTube</a>`);
      }
      
      if (socialLinks.length > 0) {
        emailContent += '<br><hr style="margin: 15px 0; border: none; border-top: 1px solid #e5e7eb;"><p style="font-weight: bold; margin: 10px 0;">Connect with us on social media:</p><p style="margin: 0;">' + socialLinks.join(' | ') + '</p>';
      }
    }

    if (formData.includeOnlinePresence && businessProfile) {
      const onlineLinks = [];
      if (businessProfile.website_url) {
        let website = businessProfile.website_url;
        if (!website.startsWith('http')) {
          website = `https://${website}`;
        }
        onlineLinks.push(`<a href="${website}" style="color: #0F766E; text-decoration: none;">Visit our website</a>`);
      }
      if (businessProfile.email) {
        onlineLinks.push(`<a href="mailto:${businessProfile.email}" style="color: #0F766E; text-decoration: none;">${businessProfile.email}</a>`);
      }
      if (businessProfile.phone_number) {
        onlineLinks.push(`<span style="color: #374151;">📞 ${businessProfile.phone_number}</span>`);
      }
      
      if (onlineLinks.length > 0) {
        emailContent += '<br><hr style="margin: 15px 0; border: none; border-top: 1px solid #e5e7eb;"><p style="font-weight: bold; margin: 10px 0;">Visit us online:</p><p style="margin: 0;">' + onlineLinks.join(' | ') + '</p>';
      }
    }

    return emailContent;
  };

  return (
    <div className="min-h-screen bg-cream py-8">
      <div className="max-w-4xl mx-auto px-4">
        <button
          onClick={() => navigate('/business/dashboard')}
          className="flex items-center text-primary hover:text-primary/80 mb-4 font-outfit"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>

        <Card className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-playfair text-3xl font-bold text-gray-900">
                Send Promotional Email
              </h1>
              <p className="text-gray-600 font-outfit">
                Send promotional emails to all your subscribers
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Subject */}
            <div>
              <Label htmlFor="subject" className="font-outfit font-semibold">
                Subject *
              </Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Enter email subject"
                className="mt-2"
              />
            </div>

            {/* Body */}
            <div>
              <Label htmlFor="body" className="font-outfit font-semibold">
                Email Body *
              </Label>
              <Textarea
                id="body"
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                placeholder="Write your promotional message here..."
                rows={10}
                className="mt-2 font-outfit"
              />
            </div>

            {/* Signature */}
            <div>
              <Label htmlFor="signature" className="font-outfit font-semibold">
                Signature
              </Label>
              <Textarea
                id="signature"
                value={formData.signature}
                onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                placeholder="Email signature"
                rows={4}
                className="mt-2 font-outfit"
              />
            </div>

            {/* Recipient Selection */}
            <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="font-outfit font-semibold text-gray-900 mb-3">
                Send To:
              </p>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="sendToAll"
                    checked={formData.sendToAll}
                    onChange={() => setFormData({ ...formData, sendToAll: true, pool_ids: null })}
                    className="w-4 h-4 text-primary"
                  />
                  <Label htmlFor="sendToAll" className="font-outfit cursor-pointer">
                    All Subscribers
                  </Label>
                </div>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="sendToPools"
                    checked={!formData.sendToAll}
                    onChange={() => setFormData({ ...formData, sendToAll: false, pool_ids: [] })}
                    className="w-4 h-4 text-primary"
                  />
                  <Label htmlFor="sendToPools" className="font-outfit cursor-pointer">
                    Selected Subscriber Pools
                  </Label>
                </div>

                {!formData.sendToAll && (
                  <div className="ml-7 mt-2 space-y-2">
                    {pools.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No pools created yet. <button onClick={() => navigate('/business/subscriber-pools')} className="text-primary underline">Create a pool</button>
                      </p>
                    ) : (
                      pools.map((pool) => (
                        <div key={pool.pool_id} className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id={`pool-${pool.pool_id}`}
                            checked={formData.pool_ids?.includes(pool.pool_id) || false}
                            onChange={(e) => {
                              const newPoolIds = e.target.checked
                                ? [...(formData.pool_ids || []), pool.pool_id]
                                : (formData.pool_ids || []).filter(id => id !== pool.pool_id);
                              setFormData({ ...formData, pool_ids: newPoolIds });
                            }}
                            className="w-4 h-4 text-primary"
                          />
                          <Label htmlFor={`pool-${pool.pool_id}`} className="font-outfit cursor-pointer text-sm">
                            {pool.pool_name} <span className="text-gray-500">({pool.member_count} members)</span>
                          </Label>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Attachments */}
            <div className="space-y-3">
              <Label className="font-outfit font-semibold text-gray-900">
                Attachments (Optional)
              </Label>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center cursor-pointer"
                >
                  <Paperclip className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Click to upload files</span>
                  <span className="text-xs text-gray-400 mt-1">PDF, Images, Documents</span>
                </label>
              </div>

              {attachments.length > 0 && (
                <div className="space-y-2">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Paperclip className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-400">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAttachment(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Options */}
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <p className="font-outfit font-semibold text-gray-900 mb-3">
                Include in Email:
              </p>
              
              <div className="flex items-center space-x-3">
                <input
                  type="radio"
                  id="includeSocial"
                  checked={formData.includeSocialMedia}
                  onChange={(e) => setFormData({ ...formData, includeSocialMedia: e.target.checked })}
                  className="w-4 h-4 text-primary"
                />
                <Label htmlFor="includeSocial" className="font-outfit cursor-pointer">
                  Include Social Media Links
                </Label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="radio"
                  id="includeOnline"
                  checked={formData.includeOnlinePresence}
                  onChange={(e) => setFormData({ ...formData, includeOnlinePresence: e.target.checked })}
                  className="w-4 h-4 text-primary"
                />
                <Label htmlFor="includeOnline" className="font-outfit cursor-pointer">
                  Include Online Presence (Website, Email)
                </Label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setShowPreview(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Preview
              </Button>
              
              <Button
                onClick={handleSendEmail}
                disabled={loading}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-outfit">Email Preview</DialogTitle>
              <DialogDescription className="font-outfit">
                This is how your email will appear to subscribers
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm text-gray-500 font-outfit mb-1">Subject:</p>
                <p className="font-semibold font-outfit">{formData.subject || '(No subject)'}</p>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-gray-500 font-outfit mb-2">Preview:</p>
                <div className="bg-white border rounded-lg p-6" style={{
                  fontFamily: 'Arial, sans-serif',
                  backgroundColor: '#f9fafb',
                  padding: '20px'
                }}>
                  <div style={{
                    maxWidth: '600px',
                    margin: '0 auto',
                    backgroundColor: 'white',
                    padding: '30px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <h2 style={{ color: '#0F766E', marginBottom: '20px' }}>
                      {businessProfile?.business_name || 'Your Business'}
                    </h2>
                    <p style={{ color: '#374151', fontSize: '16px' }}>
                      Hi [Customer Name],
                    </p>
                    <div 
                      style={{ lineHeight: '1.6', color: '#374151', margin: '20px 0' }}
                      dangerouslySetInnerHTML={{ __html: generatePreview() || '(No content)' }}
                    />
                    <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
                    <p style={{ color: '#6B7280', fontSize: '12px', margin: '0' }}>
                      You received this email because you are a subscriber of {businessProfile?.business_name || 'this business'}.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
